import { ReactNode, useEffect, useState } from 'react';
import { SplashScreen } from 'expo-router';
import { useAuth } from '~/auth/contexts/auth/Auth';
import { useTranslation } from 'react-i18next';
import { usePowerSync } from '@powersync/react';
import { useFonts } from 'expo-font';
import { logger } from '@/lib/logger';
import { restoreApolloCache } from '@/lib/apollo';
import {
  Sora_400Regular,
  Sora_500Medium,
  Sora_600SemiBold,
  Sora_700Bold,
  Sora_800ExtraBold,
} from '@expo-google-fonts/sora';
import {
  Inter_400Regular,
  Inter_500Medium,
  Inter_600SemiBold,
  Inter_700Bold,
} from '@expo-google-fonts/inter';

export interface SplashScreenProviderProps {
  children?: ReactNode;
}

export const SplashScreenProvider = ({
  children,
}: SplashScreenProviderProps) => {
  const { isLoadingAuth } = useAuth();
  const { i18n } = useTranslation();
  const powerSync = usePowerSync();

  const [fontsLoaded] = useFonts({
    Sora_400Regular,
    Sora_500Medium,
    Sora_600SemiBold,
    Sora_700Bold,
    Sora_800ExtraBold,
    Inter_400Regular,
    Inter_500Medium,
    Inter_600SemiBold,
    Inter_700Bold,
  });

  // Restore the persisted Apollo cache from disk before rendering, so the first
  // screen can read warm data instead of a cold, empty cache. `finally` (not
  // `then`) ensures a restore failure never wedges the splash.
  const [cacheRestored, setCacheRestored] = useState(false);
  useEffect(() => {
    restoreApolloCache().finally(() => setCacheRestored(true));
  }, []);

  // Always wait for PowerSync to initialize
  const powerSyncReady = !!powerSync;
  const ready =
    !isLoadingAuth &&
    i18n.isInitialized &&
    powerSyncReady &&
    fontsLoaded &&
    cacheRestored;

  useEffect(() => {
    logger.debug('SplashScreen conditions:', {
      isLoadingAuth,
      i18nInitialized: i18n.isInitialized,
      powerSyncReady,
      powerSyncValue: powerSync,
      fontsLoaded,
      cacheRestored,
      ready,
    });
  }, [
    isLoadingAuth,
    i18n.isInitialized,
    powerSyncReady,
    powerSync,
    fontsLoaded,
    cacheRestored,
    ready,
  ]);

  useEffect(() => {
    if (ready) {
      logger.debug('All conditions met, hiding splash screen');
      void SplashScreen.hideAsync();
    }
  }, [ready]);

  return ready ? <>{children}</> : null;
};
